import { Box, Button, Grid } from "@mui/material";
import { IUser, IViolationResult } from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { getViolations } from "src/API/CrewScheduleErrors";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { setFocusedCrewSchedArea, setSelectedCrewDays } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { UISettings } from "src/redux/reducers/UISettingsReducer";
import { getFirstCrewSchedWindowIdContainingCrew } from "../CrewSchedule/CrewScheduleHeader/CrewScheduleHeader";

interface IViolationErrorProps {
  id: string;
}

interface IViolationTableData {
  _id: string;
  title: string;
  regulation: string;
  link: string;
  start: number;
  end: number;
  crewId: string;
  crewMember: string;
  cause: string;
}

export const violationErrorTableInfo: ITableColumn[] = [
  {
    id: "title",
    value: "title",
    index: 0,
    numeric: false,
    label: "Title",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "regulation",
    value: "regulation",
    index: 1,
    numeric: false,
    label: "Regulation",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "cause",
    value: "cause",
    index: 2,
    numeric: false,
    label: "Cause",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "crewMember",
    value: "crewMember",
    index: 3,
    numeric: false,
    label: "Crew Member",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "start",
    value: "start",
    index: 4,
    numeric: false,
    label: "Start",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "end",
    value: "end",
    index: 5,
    numeric: false,
    label: "End",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
];

export const violationErrorsDesc = {
  tableTitle: "Violations",
  tableDescription: "",
  noMessage: "No Violations Found",
};

export const ViolationErrorTable = (props: IViolationErrorProps): JSX.Element => {
  const { id } = props;
  const dispatch = useDispatch();
  const violation = useSelector((state: IRootState) => state.crewSchedule.crewViolations);
  const appUsers = useSelector((state: IRootState) => state.structures.appUsers);
  const filteredCrew = useSelector((state: IRootState) => state.crewSchedule.filteredCrew);
  const showFutureViolationsDefault = useSelector(
    (state: IRootState) => state.UISettings.violations?.showOnlyFutureDefault ?? false
  );
  const [data, setData] = useState<IViolationTableData[]>([]);
  const isLoading = useSelector((state: IRootState) => state.crewSchedule.isLoadingCrewViolations);
  const loadedViolations = useSelector((state: IRootState) => state.crewSchedule.loadedViolations);
  const [warning, setWarning] = useState<string>("");

  const violationErrors = useMemo(() => {
    return loadedViolations ? violation : null;
  }, [violation, loadedViolations]);

  useEffect(() => {
    const getViolationsAction = (): void => {
      void dispatch(getViolations());
    };
    if (!isLoading && !loadedViolations) {
      getViolationsAction();
    }
  }, [dispatch, isLoading, loadedViolations]);

  const getData = useCallback(() => {
    const vios: IViolationTableData[] = [];
    if (!violationErrors) {
      return vios;
    }
    Object.keys(violationErrors).forEach((crewId: string) => {
      const user = appUsers.find((appUser: IUser) => appUser._id === crewId);
      const crewMember = user ? `${user.firstName} ${user.lastName} [${user.employeeId}]` : "";
      Object.values(violationErrors[crewId]?.violations).forEach((value) => {
        value.regulationResults.forEach((violation: IViolationResult) => {
          if (violation.status !== "fail" || violation.noShow || vios.find((viol) => viol._id === violation._id)) {
            return;
          }
          vios.push({
            _id: violation._id,
            title: violation.title,
            regulation: violation.regulation,
            link: violation.link ?? "",
            start: violation.period?.start,
            end: violation.period?.end,
            crewId,
            crewMember,
            cause: violation.cause ?? "",
          });
        });
      });
    });
    return vios;
  }, [appUsers, violationErrors]);

  //initially set the table data
  useEffect(() => {
    const setDataInitially = (): void => {
      const data = getData();
      if (data.length > 0) setData(data);
    };
    if (data?.length === 0 && loadedViolations && appUsers?.length > 0) {
      setDataInitially();
    }
  }, [violationErrors, data, getData, loadedViolations, appUsers]);

  const changeSelectedRows = useCallback(
    (rows: any, focusSelected?: boolean, dataType?: string) => {
      const row = rows[0];
      const crewId = row?.crewId;
      const selectedDay = moment.utc(row?.start).startOf("day")?.valueOf();
      const endOfDay = moment.utc(row?.end).endOf("day")?.valueOf();
      if (focusSelected) {
        const windowToFocus = getFirstCrewSchedWindowIdContainingCrew(crewId, filteredCrew);
        if (!!windowToFocus || !crewId) {
          setWarning("");
        } else {
          setWarning("You have no open crew schedule window displaying this crew member");
        }
        if (windowToFocus) {
          dispatch(
            setFocusedCrewSchedArea(
              {
                timeInterval: [selectedDay, endOfDay],
                crewIds: [crewId],
              },
              windowToFocus
            )
          );
        }
      }
      dispatch(setSelectedCrewDays({ crewId, selectedTimes: [selectedDay], timezone: "Etc/UTC" }));
    },
    [dispatch, filteredCrew]
  );

  const tableData = useMemo(() => {
    const now = new Date().valueOf();
    const filterData = showFutureViolationsDefault ? data.filter((d) => d.start > now) : data;
    return [
      {
        id: "violationErrors",
        dataType: "ViolationErrors",
        label: "Violations",
        data: filterData,
        tableInfo: violationErrorTableInfo,
        tableInfoDisplay: violationErrorsDesc,
      },
    ];
  }, [data, showFutureViolationsDefault]);

  if (!data || data.length === 0) {
    return (
      <Box sx={{ height: "100%", width: "100%" }}>
        <SpinnerProgress loading={isLoading || !loadedViolations} message="loading" />
        {loadedViolations ? "No Violations Found" : ""}
      </Box>
    );
  }

  return (
    <Grid item xs={12} className="errors_table__block">
      {!!warning && <span className="error_table_warning">{warning}</span>}
      <FilterSearchTable
        className="error_table"
        id={`violation_errors_table_${id}`}
        parentId={`${id}-panel`}
        events={tableData}
        uniqueKey={"id"}
        changeSelectedRows={changeSelectedRows}
        selectModeSingle={true}
        hideFilter={false}
        defaultRowsPerPage={10}
        optionalControls={
          <div className="optional_controls">
            <Button
              className="refresh_btn"
              id={"refresh_violationsdata_btn"}
              onClick={() => {
                const data = getData();
                setData(data);
              }}
              color="primary"
              variant="contained"
            >
              {"Refresh"}
            </Button>
            <CheckboxField
              className="future_violations_checkbox"
              label="Only Future Violations"
              name="future_violations_checkbox"
              checked={showFutureViolationsDefault}
              disabled={!data || isLoading}
              onChange={() => {
                const newValue = !showFutureViolationsDefault;
                dispatch(UISettings.actions.setViolationsShowOnlyFutureDefault(newValue));
              }}
              labelPlacement="end"
              hideFocus={true}
            />
          </div>
        }
      />
    </Grid>
  );
};
